
import React, { useState } from 'react';
// Fix: Use namespace import to bypass missing exported member errors in react-router-dom
import * as ReactRouterDom from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { AppSettings } from '../types';
import { 
  LogOut, Users, Briefcase, Home, 
  Menu, Loader2, Building2, X, Database, Settings2, BarChart3, Target, Activity
} from 'lucide-react';

const { Link, useLocation, Outlet } = ReactRouterDom as any;

export const DashboardLayout: React.FC = () => {
  const { profile, isProfileSyncing } = useAuth();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const { data: settings } = useQuery({
    queryKey: ['appSettingsLayout'],
    queryFn: async () => {
      const { data } = await supabase.from('app_settings').select('*').single();
      return data as AppSettings;
    }
  });

  const appName = "HFLI Data";

  const navItems = [
    { label: 'Dashboard', icon: Home, path: '/' },
    { label: 'Programs', icon: Briefcase, path: '/programs' },
    { label: 'People', icon: Users, path: '/people' },
    { label: 'Organizations', icon: Building2, path: '/organizations' },
    { label: 'Engagements', icon: Target, path: '/engagements' },
    { label: 'Sessions', icon: Activity, path: '/sessions' },
    { label: 'Reports', icon: BarChart3, path: '/reports' },
  ];

  const isAdmin = profile?.role === 'system_admin';
  const isManager = profile?.role === 'system_admin' || profile?.role === 'org_manager';

  if (isManager) {
    navItems.push({ 
      label: profile?.role === 'system_admin' ? 'Admin Control' : 'Settings', 
      icon: Settings2, 
      path: '/admin' 
    });
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col lg:flex-row overflow-hidden h-screen">
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Wide w-80 */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-80 bg-black border-r border-slate-900 transform transition-transform duration-500 ease-out lg:static lg:translate-x-0 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="h-full flex flex-col p-8">
          <div className="mb-12 flex items-center justify-center relative">
             <Link to="/" className="flex flex-col items-center gap-4">
               {settings?.branding?.logo_url ? (
                 <img src={settings.branding.logo_url} className="w-16 h-16 object-contain" alt="Logo" />
               ) : (
                 <div className="w-16 h-16 bg-[#FFDB00] rounded-2xl flex items-center justify-center text-black shadow-lg">
                   <Database size={32} />
                 </div>
               )}
             </Link>
             <button onClick={() => setIsSidebarOpen(false)} className="lg:hidden absolute -right-2 top-0 text-slate-400 hover:text-white p-2 transition-colors">
               <X size={24} />
             </button>
          </div>

          <nav className="flex-1 space-y-2">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsSidebarOpen(false)}
                  className={`flex items-center gap-4 px-6 py-4 rounded-xl font-black uppercase tracking-widest text-[10px] transition-all
                    ${isActive ? 'bg-[#FFDB00] text-black shadow-lg shadow-[#FFDB00]/10' : 'text-slate-500 hover:bg-white/5 hover:text-white'}`}
                >
                  <item.icon size={20} strokeWidth={isActive ? 3 : 2} />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="pt-8 border-t border-slate-900 space-y-6">
            <div className="flex items-center gap-4 px-4 py-4 bg-white/5 rounded-2xl border border-white/5">
              <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center text-[#FFDB00] font-black text-xs border border-slate-800 shadow-xl relative shrink-0">
                {isProfileSyncing && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-black rounded-full flex items-center justify-center border border-slate-800">
                    <Loader2 size={10} className="animate-spin text-[#FFDB00]" />
                  </div>
                )}
                {profile ? (profile.full_name?.charAt(0) || profile.email?.charAt(0)) : '?'}
              </div>
              <div className="overflow-hidden">
                <p className="font-black text-white truncate uppercase text-[10px] tracking-tight leading-none mb-1">{profile?.full_name || 'Staff'}</p>
                <p className="text-[8px] text-slate-500 font-black truncate uppercase tracking-[0.2em] leading-none">{profile?.role?.replace('_', ' ')}</p>
              </div>
            </div>
            <button 
              onClick={() => supabase.auth.signOut()}
              className="w-full flex items-center gap-4 px-6 py-4 text-rose-500 font-black uppercase tracking-[0.3em] text-[10px] hover:bg-rose-500/10 rounded-xl transition-all"
            >
              <LogOut size={20} />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-h-0 relative border-l border-slate-200">
        <header className="sticky top-0 z-30 w-full bg-white border-b border-slate-200 h-24 flex items-center justify-between px-6 lg:px-16 shrink-0">
          <div className="flex items-center gap-4 overflow-hidden">
            <button onClick={() => setIsSidebarOpen(true)} className="lg:hidden p-3 text-slate-600 hover:bg-slate-100 rounded-xl transition-colors shrink-0">
              <Menu size={24} />
            </button>
            <div className="flex items-center gap-3">
               {/* Mobile Logo Visibility */}
               <div className="lg:hidden w-8 h-8">
                  {settings?.branding?.logo_url ? (
                    <img src={settings.branding.logo_url} className="w-full h-full object-contain" alt="Logo" />
                  ) : (
                    <Database size={24} className="text-slate-900" />
                  )}
               </div>
               <h1 className="text-xl lg:text-2xl font-black text-slate-900 uppercase tracking-tighter truncate">{appName}</h1>
            </div>
          </div>
          <div className="flex items-center gap-4 shrink-0">
             <div className="px-4 py-1.5 bg-slate-50 border border-slate-200 rounded-full text-[9px] font-black text-slate-400 uppercase tracking-widest shadow-inner">Build v2.5.0</div>
             {profile?.full_name && (
               <div className="hidden lg:flex items-center gap-3 px-4 py-1.5 bg-slate-900 rounded-full shadow-lg">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-[9px] font-black text-[#FFDB00] uppercase tracking-[0.2em]">{profile.full_name}</span>
               </div>
             )}
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6 sm:p-10 lg:p-16 xl:p-20 no-scrollbar">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
